import { calculateDistance } from "./mapUtils";
import {
  MOVEMENT_THRESHOLD,
  DEFAULT_DEVIATION_DISTANCE_METERS,
  OFF_ROUTE_CONFIRMATION_POINTS,
  ON_ROUTE_CONFIRMATION_POINTS,
  ROUTE_DEVIATION_HYSTERESIS_METERS,
  ACTUAL_PATH_MAX_POINTS,
  ACTUAL_PATH_MAX_SPEED_KMH,
} from "./mapConfig";

export function normalizeGpsPoint(raw) {
  if (!raw) return null;
  const lat = parseFloat(raw.lat ?? raw.latitude ?? raw.Latitude);
  const lng = parseFloat(raw.lng ?? raw.longitude ?? raw.Longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  if (lat === 0 && lng === 0) return null;

  const rawTs =
    raw.timestamp ?? raw.recordedAt ?? raw.createdAt ?? raw.eventAt;
  let timestamp = Date.now();
  if (rawTs !== undefined && rawTs !== null && rawTs !== "") {
    const numeric = Number(rawTs);
    if (Number.isFinite(numeric)) {
      timestamp = numeric < 1e12 ? numeric * 1000 : numeric;
    } else {
      const parsed = new Date(rawTs).getTime();
      if (Number.isFinite(parsed)) timestamp = parsed;
    }
  }

  return { lat, lng, timestamp };
}

export function distanceToPolyline(point, fullPath) {
  const origin = normalizeGpsPoint(point);
  if (!origin || !Array.isArray(fullPath) || fullPath.length < 2) return null;

  let minDistance = Infinity;
  for (let index = 1; index < fullPath.length; index += 1) {
    const start = fullPath[index - 1];
    const end = fullPath[index];
    if (!start || !end) continue;
    const distance = distancePointToSegment(origin, start, end);
    if (Number.isFinite(distance) && distance < minDistance) {
      minDistance = distance;
    }
  }

  return Number.isFinite(minDistance) ? minDistance : null;
}

export function nearestPointOnPath(point, fullPath) {
  const origin = normalizeGpsPoint(point);
  if (!origin || !Array.isArray(fullPath) || fullPath.length < 2) return null;

  let nearest = null;
  let minDistance = Infinity;
  for (let index = 1; index < fullPath.length; index += 1) {
    const start = fullPath[index - 1];
    const end = fullPath[index];
    if (!start || !end) continue;
    const dx = end.lng - start.lng;
    const dy = end.lat - start.lat;
    const segmentLengthSq = dx * dx + dy * dy;
    const t =
      segmentLengthSq === 0
        ? 0
        : Math.max(
            0,
            Math.min(
              1,
              ((origin.lng - start.lng) * dx + (origin.lat - start.lat) * dy) /
                segmentLengthSq,
            ),
          );
    const projected = {
      lat: start.lat + t * dy,
      lng: start.lng + t * dx,
    };
    const distance = calculateDistance(origin, projected);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = projected;
    }
  }
  return nearest;
}

function distancePointToSegment(point, start, end) {
  const dx = end.lng - start.lng;
  const dy = end.lat - start.lat;
  const segmentLengthSq = dx * dx + dy * dy;
  if (segmentLengthSq === 0) {
    return calculateDistance(point, start);
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.lng - start.lng) * dx + (point.lat - start.lat) * dy) /
        segmentLengthSq,
    ),
  );
  const projected = {
    lat: start.lat + t * dy,
    lng: start.lng + t * dx,
  };
  return calculateDistance(point, projected);
}

function capTrail(trail, maxPoints) {
  if (!maxPoints || trail.length <= maxPoints) return trail;
  const keepFirst = trail[0];
  const rest = trail.slice(trail.length - (maxPoints - 1));
  if (rest[0] === keepFirst) return rest;
  return [keepFirst, ...rest];
}

export function appendSmoothedPoint(trail, point, opts = {}) {
  const normalized = normalizeGpsPoint(point);
  if (!normalized) return Array.isArray(trail) ? trail : [];

  const list = Array.isArray(trail) ? trail : [];
  const minDistanceMeters = opts.minDistanceMeters ?? MOVEMENT_THRESHOLD;
  const maxSpeedKmh = opts.maxSpeedKmh ?? ACTUAL_PATH_MAX_SPEED_KMH;
  const maxPoints = opts.maxPoints ?? ACTUAL_PATH_MAX_POINTS;

  if (opts.force || list.length === 0) {
    return capTrail([...list, normalized], maxPoints);
  }

  const previous = list[list.length - 1];
  const dist = calculateDistance(previous, normalized);
  if (dist < minDistanceMeters) return list;

  const dtMs = (normalized.timestamp || 0) - (previous.timestamp || 0);
  if (dtMs >= 250) {
    const speedKmh = dist / 1000 / (dtMs / 3_600_000);
    if (speedKmh > maxSpeedKmh) return list;
  } else if (dist > 500) {
    return list;
  }

  return capTrail([...list, normalized], maxPoints);
}

export function createTrailMachine(options = {}) {
  return {
    thresholdMeters:
      options.thresholdMeters ?? DEFAULT_DEVIATION_DISTANCE_METERS,
    confirmOffPoints:
      options.confirmOffPoints ?? OFF_ROUTE_CONFIRMATION_POINTS,
    confirmOnPoints: options.confirmOnPoints ?? ON_ROUTE_CONFIRMATION_POINTS,
    hysteresisMeters:
      options.hysteresisMeters ?? ROUTE_DEVIATION_HYSTERESIS_METERS,
    minDistanceMeters: options.minDistanceMeters ?? MOVEMENT_THRESHOLD,
    maxSpeedKmh: options.maxSpeedKmh ?? ACTUAL_PATH_MAX_SPEED_KMH,
    maxPoints: options.maxPoints ?? ACTUAL_PATH_MAX_POINTS,
    state: "ON_ROUTE",
    offRouteCount: 0,
    onRouteCount: 0,
    lastOnRoutePoint: null,
    pendingOff: [],
    segments: [],
    openSegment: null,
    lastPoint: null,
  };
}

function openSegment(machine, point, fullPath) {
  const smoothOpts = trailSmoothOpts(machine);
  let segment = [];
  const departure = machine.lastOnRoutePoint || point;
  const anchor = nearestPointOnPath(departure, fullPath);
  if (anchor) {
    segment = appendSmoothedPoint(
      segment,
      {
        lat: anchor.lat,
        lng: anchor.lng,
        timestamp: (departure.timestamp || point.timestamp || Date.now()) - 1,
      },
      { ...smoothOpts, force: true },
    );
  }
  if (machine.lastOnRoutePoint) {
    segment = appendSmoothedPoint(segment, machine.lastOnRoutePoint, smoothOpts);
  }
  for (const pending of machine.pendingOff) {
    segment = appendSmoothedPoint(segment, pending, smoothOpts);
  }
  segment = appendSmoothedPoint(segment, point, {
    ...smoothOpts,
    force: segment.length === 0,
  });
  machine.openSegment = segment;
  machine.pendingOff = [];
  machine.state = "OFF_ROUTE";
  machine.offRouteCount = machine.confirmOffPoints;
  machine.onRouteCount = 0;
}

function closeSegment(machine, point, fullPath) {
  if (machine.openSegment) {
    const smoothOpts = trailSmoothOpts(machine);
    let closed = appendSmoothedPoint(machine.openSegment, point, smoothOpts);
    const anchor = nearestPointOnPath(point, fullPath);
    if (anchor) {
      closed = appendSmoothedPoint(
        closed,
        {
          lat: anchor.lat,
          lng: anchor.lng,
          timestamp: (point.timestamp || Date.now()) + 1,
        },
        { ...smoothOpts, force: true },
      );
    }
    if (closed.length >= 2) {
      machine.segments.push(closed);
    }
  }
  machine.openSegment = null;
  machine.pendingOff = [];
  machine.state = "ON_ROUTE";
  machine.onRouteCount = machine.confirmOnPoints;
  machine.offRouteCount = 0;
  machine.lastOnRoutePoint = point;
}

function trailSmoothOpts(machine) {
  return {
    minDistanceMeters: machine.minDistanceMeters,
    maxSpeedKmh: machine.maxSpeedKmh,
    maxPoints: machine.maxPoints,
  };
}

function isDuplicatePoint(machine, point) {
  if (!machine.lastPoint) return false;
  const dt = Math.abs((point.timestamp || 0) - (machine.lastPoint.timestamp || 0));
  return dt < 1000 && calculateDistance(machine.lastPoint, point) < 1;
}

export function applyTrailPoint(machine, rawPoint, signals = {}) {
  const point = normalizeGpsPoint(rawPoint);
  if (!machine || !point) return machine;
  if (isDuplicatePoint(machine, point)) return machine;

  const eventType = signals.eventType;
  const snapshotState = signals.snapshotState;
  const fullPath = signals.fullPath;

  if (eventType === "OUT_OF_ROUTE") {
    if (machine.state !== "OFF_ROUTE") {
      openSegment(machine, point, fullPath);
    } else {
      machine.openSegment = appendSmoothedPoint(
        machine.openSegment,
        point,
        trailSmoothOpts(machine),
      );
    }
    machine.lastPoint = point;
    return machine;
  }

  if (eventType === "BACK_ON_ROUTE") {
    if (machine.state === "OFF_ROUTE") {
      closeSegment(machine, point, fullPath);
    } else {
      machine.lastOnRoutePoint = point;
    }
    machine.lastPoint = point;
    return machine;
  }

  if (snapshotState === "OFF_ROUTE") {
    if (machine.state !== "OFF_ROUTE") {
      openSegment(machine, point, fullPath);
    } else {
      machine.openSegment = appendSmoothedPoint(
        machine.openSegment,
        point,
        trailSmoothOpts(machine),
      );
    }
    machine.lastPoint = point;
    return machine;
  }

  if (snapshotState === "ON_ROUTE") {
    if (machine.state === "OFF_ROUTE") {
      closeSegment(machine, point, fullPath);
    } else {
      machine.lastOnRoutePoint = point;
    }
    machine.lastPoint = point;
    return machine;
  }

  const distanceMeters = distanceToPolyline(point, fullPath);
  if (distanceMeters == null) {
    machine.lastPoint = point;
    return machine;
  }

  const reentryThreshold = Math.max(
    0,
    machine.thresholdMeters - machine.hysteresisMeters,
  );
  const isOutside = distanceMeters > machine.thresholdMeters;
  const isConfirmedInside = distanceMeters <= reentryThreshold;

  if (isOutside) {
    machine.offRouteCount += 1;
    machine.onRouteCount = 0;
    machine.pendingOff = [...machine.pendingOff, point].slice(
      -machine.confirmOffPoints,
    );
  } else if (isConfirmedInside) {
    machine.onRouteCount += 1;
    machine.offRouteCount = 0;
    machine.pendingOff = [];
    machine.lastOnRoutePoint = point;
  } else {
    machine.offRouteCount = 0;
    machine.onRouteCount = 0;
    machine.pendingOff = [];
  }

  if (machine.state === "ON_ROUTE") {
    if (machine.offRouteCount >= machine.confirmOffPoints) {
      openSegment(machine, point, fullPath);
    }
  } else if (machine.openSegment) {
    machine.openSegment = appendSmoothedPoint(
      machine.openSegment,
      point,
      trailSmoothOpts(machine),
    );
    if (machine.onRouteCount >= machine.confirmOnPoints) {
      closeSegment(machine, point, fullPath);
    }
  }

  machine.lastPoint = point;
  return machine;
}

export function splitOffRouteSegments(points, fullPath, options = {}) {
  const machine = createTrailMachine(options);
  for (const raw of points || []) {
    applyTrailPoint(machine, raw, { fullPath });
  }
  return machine;
}

// Keep only the parts of a path that have left the planned route.
// Points on the planned route are not drawn.
export function clipPathBeyondThreshold(path, plannedPath, thresholdMeters) {
  const parsed = Number(thresholdMeters);
  const threshold =
    Number.isFinite(parsed) && parsed > 0
      ? parsed
      : DEFAULT_DEVIATION_DISTANCE_METERS;
  const segments = [];
  let current = [];

  (path || []).forEach((point) => {
    const distance = distanceToPolyline(point, plannedPath);
    if (distance != null && distance > threshold) {
      current.push(point);
      return;
    }
    if (current.length > 1) segments.push(current);
    current = [];
  });

  if (current.length > 1) segments.push(current);
  return segments;
}

export function getRenderableSegments(machine) {
  if (!machine) return [];
  const segments = machine.segments.filter((segment) => segment.length >= 2);
  if (machine.openSegment && machine.openSegment.length >= 2) {
    return [...segments, machine.openSegment];
  }
  return segments;
}
